/**
 * What the coach page shows around a plateau's rep range and its deload
 * (#272, PC-I107): the two offers that change today's targets rather than
 * write a template, so an accepted one is an adjustment (ADR 0005), derived
 * from the answer and never stored beside it.
 *
 * The same opener shape as an add offer (`addOfferPresentation.ts`, PC-I75):
 * an amber pill that accepts, "I'm happy holding this" that declines and
 * folds the line away (PC-I41), one scope line, and after a yes a record line
 * with Undo, which the server takes until a live session starts under the
 * answer (PC-I111, so Undo follows `can_change`). Offered only where the
 * user's phone shows adjustments (PC-I112), so "Each set shows why" is true.
 *
 * Pure, so the rules are tested rather than judged on a screen. The iOS twin
 * is GroundworkKit's `AdjustOfferPresentation`, and the words both render come
 * from the shared deck (`COACH_COPY` / `CoachCopy`), which
 * `scripts/check_copy_decks.py` keeps byte-identical.
 */
import type { OpenerOffer } from '../../api/types'
import { COACH_COPY } from '../../copy/coach'
import { fill } from '../../copy/firstRun'
import type { OfferView } from './offerPresentation'

/** The kinds this renderer draws. */
export const ADJUST_OFFER_KINDS = ['rep_range', 'deload'] as const

/** Whether this renderer draws an offer of this kind. */
export function isAdjustOffer(kind: string | null | undefined): boolean {
  return kind != null && (ADJUST_OFFER_KINDS as readonly string[]).includes(kind)
}

/** A whole positive number in an offer's detail, or null. */
function detailCount(offer: OpenerOffer, key: string): number | null {
  const value = offer.detail?.[key]
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null
}

/**
 * The words an offer's numbers fill, or null when the detail does not carry
 * them: a button that cannot say what it changes is not offered.
 */
function words(offer: OpenerOffer): { accept: string; scope: string; record: string } | null {
  if (offer.kind === 'rep_range') {
    const low = detailCount(offer, 'reps_min')
    const high = detailCount(offer, 'reps_max')
    const weeks = detailCount(offer, 'weeks')
    if (low === null || high === null || weeks === null) return null
    return {
      accept: fill(COACH_COPY.adjustOfferAcceptRange, { low, high }),
      scope: fill(COACH_COPY.adjustOfferScopeRange, { weeks }),
      record: fill(COACH_COPY.adjustOfferRangeRecord, { low, high, weeks }),
    }
  }
  if (offer.kind === 'deload') {
    const days = detailCount(offer, 'days')
    const share = detailCount(offer, 'share_pct')
    if (days === null || share === null) return null
    return {
      accept: COACH_COPY.adjustOfferAcceptDeload,
      scope: fill(COACH_COPY.adjustOfferScopeDeload, { days, share: `${share}%` }),
      record: COACH_COPY.adjustOfferDeloadRecord,
    }
  }
  return null
}

/**
 * Which controls a rep range or a deload gets (PC-I111, PC-I130).
 *
 * The offer while it is unanswered and can be answered. After a yes the
 * record line says what was set, with Undo while the server would take it
 * (`can_change`). A decline folds the line away, so it draws no record line.
 */
export function adjustOfferView(offer: OpenerOffer | null | undefined): OfferView {
  if (!offer || !isAdjustOffer(offer.kind)) return { kind: 'none' }
  const said = words(offer)
  if (said === null) return { kind: 'none' }
  if (offer.answer === 'accepted') {
    return { kind: 'answered', record: said.record, undo: offer.can_change !== false }
  }
  if (offer.answer != null || offer.can_change === false) return { kind: 'none' }
  return {
    kind: 'offer',
    accept: said.accept,
    decline: COACH_COPY.addOfferHoldingThis,
    scope: said.scope,
  }
}
